
import { useState, useEffect } from 'react';
import { LeadFormData } from "@/types/lead";
import { saveLead } from "@/utils/saveLead";
import { toast } from "@/utils/toast";
import { 
  validateLeadForm, 
  sanitizeFormData, 
  formatWhatsApp, 
  formatCNPJ 
} from "@/utils/formValidation";
import { 
  trackFormSubmission,
  getCurrentUTMParams 
} from "@/utils/googleAds";
import { testSupabaseConnection } from "@/utils/testSupabaseConnection";
import { getCachedWhatsAppSettings } from "@/utils/whatsapp/whatsAppSettings";
import { createWhatsAppLinkFromSettings } from "@/utils/whatsapp/createWhatsAppLink";
import { supabase } from "@/integrations/supabase/client";

export const useCalculatorState = () => {
  const initialFormData: LeadFormData = {
    name: '',
    whatsapp: '',
    cnpj: ''
  };

  const [formData, setFormData] = useState<LeadFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Test Supabase connection only in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      testSupabaseConnection();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === 'whatsapp') {
      formattedValue = formatWhatsApp(value);
    } else if (name === 'cnpj') {
      formattedValue = formatCNPJ(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };
  
  const validateForm = () => {
    const validation = validateLeadForm(formData);
    if (!validation.isValid) {
      return validation.errors[0];
    }
    return null;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setIsSubmitting(true);

    const sanitizedData = sanitizeFormData(formData);

    // 1. Build WhatsApp URL synchronously (from cache/defaults)
    const settings = getCachedWhatsAppSettings();
    const url = createWhatsAppLinkFromSettings(settings, '', false);

    // 2. Open WhatsApp IMMEDIATELY in the user's click tick (avoids popup blocker)
    window.open(url, '_blank');

    // 3. Tracking (synchronous)
    const utmParams = getCurrentUTMParams();
    trackFormSubmission(sanitizedData, utmParams);

    // 4. Reset form to initial state
    setFormData(initialFormData);
    setIsSubmitting(false);

    // 5. Background: save lead and track click (fire-and-forget)
    const extendedFormData = {
      ...sanitizedData,
      email: '',
      city: '',
      state: '',
      emitsInvoice: false,
      monthlyRevenue: 0,
      taxRegime: 'Não informado' as const
    };

    saveLead(extendedFormData, true)
      .then(result => {
        console.log("✅ Lead saved:", result?.id);
      })
      .catch(err => {
        console.error("💥 Error saving lead:", err);
      });

    supabase.from('whatsapp_clicks').insert({ source: 'form_redirect' })
      .then(({ error: dbErr }) => {
        if (dbErr) console.error('Erro ao trackear clique WhatsApp:', dbErr);
      });
  };
  
  const handleReset = () => {
    setFormData(initialFormData);
  };

  return {
    formData,
    isSubmitting,
    actions: {
      handleChange,
      handleSubmit,
      handleReset
    }
  };
};
